import {
    useCallback, useRef,
} from 'react'
import {
    FormControl,
} from '@mui/material'
import noop from 'lodash/noop'
import isEmpty from 'lodash/isEmpty'

import useEnumValues from 'app/hooks/useEnumValues'
import Checkbox from 'app/shared-components/Checkbox'
import Label from 'app/shared-components/Label'
import EnumSelector from 'app/shared-components/EnumSelector'
import useLabel from 'app/hooks/useLabel'
import {
    ContainerAvailabilityAllowedProductTypes,
} from 'app/enums/label.enums'
import {
    ContainerAvailabilityForecastIncludeOptions,
} from 'app/enums/common.enums'
import {
    ForecastAvailabilityOptions,
} from 'app/Apps/ContainerManagement/ServiceCenters/serviceCenters.types'

import {
    containerStatusLabelMap,
    includeLabelMap,
} from '../ContainerAvailabilityForecast.utils'
import useStyles from './OptionPanel.style'

const OptionPanel = ({
    value,
    onChange,
    allowedValues,
    includesOrders,
    setIncludesOrders = noop,
}: Readonly<{
    value: ForecastAvailabilityOptions,
    onChange: (value: ForecastAvailabilityOptions) => void,
    allowedValues: Record<string, string>,
    includesOrders?: Partial<Record<ContainerAvailabilityForecastIncludeOptions, boolean>>,
    setIncludesOrders?: (newVal: Record<string, boolean>) => void,
}>) => {
    const {
        classes,
    } = useStyles()

    const l = useLabel()

    const listStatuses = useRef(Object.keys(value.containerStatus))

    const onChangeIncludesOrders = useCallback((newVal: boolean, {
        target: {
            name,
        },
    }) => {
        setIncludesOrders({
            ...includesOrders,
            [name]: newVal,
        })
    }, [
        includesOrders,
        setIncludesOrders,
    ])

    const onChangeContainerStatus = useCallback((newVal: boolean, {
        target: {
            name,
        },
    }) => {
        onChange({
            ...value,
            containerStatus: {
                ...value.containerStatus,
                [name]: newVal,
            },
        })
    }, [
        onChange,
        value,
    ])

    const onChangeProductType = useCallback((newVal: ContainerAvailabilityAllowedProductTypes) => {
        onChange({
            ...value,
            productType: newVal,
        })
    }, [
        onChange,
        value,
    ])

    const allowedProductTypes = useEnumValues()(allowedValues)

    return (
        <div className={classes.panel}>
            <FormControl className={classes.containerStatus}>
                <Label title={l('CONTAINER_STATUS')} />
                <div className={classes.wrapperCheckbox}>
                    {
                        listStatuses.current.map((item) => {
                            return (
                                <Checkbox
                                    key={item}
                                    name={item}
                                    title={l(containerStatusLabelMap[item] || item)}
                                    value={value.containerStatus[item]}
                                    onChange={onChangeContainerStatus}
                                    className={classes.checkbox}
                                />
                            )
                        })
                    }
                </div>
            </FormControl>
            <EnumSelector
                title={l('PRODUCT_TYPE')}
                className={classes.productType}
                value={value.productType}
                allowedValues={allowedProductTypes}
                onChange={onChangeProductType}
                name="productType"
                useDropdown
                noNone
                noSort
            />
            {includesOrders && !isEmpty(includesOrders) && (
                <FormControl className={classes.containerStatus}>
                    <Label title={l('INCLUDE')} />
                    <div className={classes.wrapperCheckbox}>
                        {
                            Object.keys(includesOrders).map((key) => {
                                return (
                                    <Checkbox
                                        name={key}
                                        title={l(includeLabelMap[key] || key)}
                                        value={includesOrders?.[key]}
                                        onChange={onChangeIncludesOrders}
                                        className={classes.checkbox}
                                        key={key}
                                    />
                                )
                            })
                        }
                    </div>
                </FormControl>
            )}
        </div>
    )
}

export default OptionPanel
